import { useState } from 'react'
import { Avatar, XStack } from 'tamagui'
import { useToastController } from '@tamagui/toast'
import { Button } from '@components/Button'
import { SectionCard } from '@components/Profile/SectionCard'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import {
  ImagePickerUnavailableError,
  useAvatarUpload,
} from '@hooks/useAvatarUpload'
import { useUserUpdate } from '@services/api/graphql/mutations/userUpdate'

interface IAvatarSectionProps {
  coverImageUrl: string | null
}

export function AvatarSection({ coverImageUrl }: IAvatarSectionProps) {
  const { t } = useComponentTranslation('Profile')
  const toast = useToastController()
  const { pickAndUpload, uploading } = useAvatarUpload()
  const { handleUpdateUser, loading } = useUserUpdate()
  const [currentUrl, setCurrentUrl] = useState(coverImageUrl)

  async function handleChangeAvatar() {
    try {
      const cdnUrl = await pickAndUpload()
      if (!cdnUrl) return

      const result = await handleUpdateUser({ coverImageUrl: cdnUrl })
      if (result?.errors?.length) {
        toast.show(t('toast.errorTitle'), {
          message: result.errors.join('\n'),
          type: 'error',
        })
        return
      }

      setCurrentUrl(cdnUrl)
      toast.show(t('toast.successTitle'), {
        message: t('avatar.successMessage'),
      })
    } catch (error) {
      if (error instanceof ImagePickerUnavailableError) {
        // Orientação (atualizar o app), não erro — toast neutro.
        toast.show(t('toast.errorTitle'), {
          message: t('avatar.updateAppMessage'),
        })
        return
      }
      toast.show(t('toast.errorTitle'), {
        message: t('avatar.errorMessage'),
        type: 'error',
      })
    }
  }

  return (
    <SectionCard title={t('avatar.title')}>
      <XStack gap="$size.spacing-md" alignItems="center">
        <Avatar circular size={96} borderColor="$border-strong" borderWidth={1}>
          {currentUrl ? (
            <Avatar.Image accessibilityLabel="avatar" src={currentUrl} />
          ) : null}
          <Avatar.Fallback backgroundColor="$bg-surface-strong" />
        </Avatar>

        <Button
          variant="secondary"
          loading={uploading || loading}
          onPress={handleChangeAvatar}
        >
          {t('avatar.change')}
        </Button>
      </XStack>
    </SectionCard>
  )
}
